import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  ArrowLeft,
  Trash2,
  Upload,
  ExternalLink,
  Plus,
  Calendar,
  Users,
  LogOut,
  Building2,
  Kanban,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { adminApi, clearAdminToken } from "@/lib/adminApi";
import { LOGO_URL } from "@/lib/content";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function AdminVenueEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [venue, setVenue] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [v, e] = await Promise.all([
        adminApi.get(`/admin/venues/${id}`),
        adminApi.get(`/admin/venues/${id}/events`),
      ]);
      setVenue(v.data);
      setEvents(e.data);
    } catch {
      toast.error("Venue not found.");
      navigate("/admin/venues");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
     
  }, [id]);

  const patchVenue = async (patch) => {
    setSaving(true);
    try {
      const { data } = await adminApi.patch(`/admin/venues/${id}`, patch);
      setVenue(data);
      toast.success("Saved.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Save failed.";
      toast.error(typeof detail === "string" ? detail : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const deleteVenue = async () => {
    if (!window.confirm(`Delete "${venue.name}" and all its events? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/venues/${id}`);
      toast.success("Venue deleted.");
      navigate("/admin/venues");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const uploadImage = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await adminApi.post(`/admin/venues/${id}/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setVenue(data);
      toast.success(`${kind === "logo" ? "Logo" : "Cover photo"} uploaded.`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Upload failed.";
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    }
  };

  const addEvent = async (payload) => {
    try {
      const { data } = await adminApi.post(`/admin/venues/${id}/events`, payload);
      setEvents((es) => [...es, data].sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at)));
      toast.success("Event added.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to add event.";
      toast.error(typeof detail === "string" ? detail : "Failed to add event.");
      throw err;
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await adminApi.delete(`/admin/venues/${id}/events/${eventId}`);
      setEvents((es) => es.filter((e) => e.id !== eventId));
      toast.success("Event deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const logout = () => {
    clearAdminToken();
    navigate("/admin/login", { replace: true });
  };

  if (loading || !venue) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-[#D9A6F5]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-venue-edit-page" className="min-h-screen bg-black text-[#F5F5F0]">
      {/* HEADER */}
      <header className="border-b border-white/10 sticky top-0 z-30 bg-black/85 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto px-6 md:px-8 flex items-center justify-between h-20">
          <div className="flex items-center gap-6">
            <Link to="/admin">
              <img src={LOGO_URL} alt="Swingle" className="h-12 w-auto object-contain" />
            </Link>
            <nav className="flex items-center gap-4">
              <Link to="/admin" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#A3A3A3] hover:text-[#D9A6F5] transition-colors">
                <Kanban size={14} /> Pipeline
              </Link>
              <Link to="/admin/venues" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#D9A6F5]">
                <Building2 size={14} /> Venues
              </Link>
              <Link to="/admin/contacts" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#A3A3A3] hover:text-[#D9A6F5] transition-colors">
                <Users size={14} /> Contacts
              </Link>
            </nav>
          </div>
          <button
            type="button"
            data-testid="venue-edit-logout"
            onClick={logout}
            className="inline-flex items-center gap-2 text-[#A3A3A3] hover:text-[#D9A6F5] px-3 py-2 text-xs uppercase tracking-[0.2em]"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 md:px-8 py-8">
        {/* Breadcrumb + actions */}
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/admin/venues"
            data-testid="venue-edit-back"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#A3A3A3] hover:text-[#D9A6F5] transition-colors"
          >
            <ArrowLeft size={14} /> All venues
          </Link>
          <div className="flex items-center gap-3">
            <a
              href={`/v/${venue.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="venue-edit-view-public"
              className="inline-flex items-center gap-2 border border-white/20 text-[#F5F5F0] hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-4 py-2 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
            >
              <ExternalLink size={12} /> View public page
            </a>
            <InviteUserButton venueId={venue.id} />
            <button
              type="button"
              data-testid="venue-edit-delete"
              onClick={deleteVenue}
              className="inline-flex items-center gap-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 px-4 py-2 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
            >
              <Trash2 size={12} /> Delete
            </button>
          </div>
        </div>

        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-3">Venue</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">{venue.name}</h1>
          <div className="mt-2 text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-mono">
            /v/{venue.slug}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Images */}
          <Card title="Logo & cover photo" testId="venue-images-card">
            <div className="grid grid-cols-2 gap-4">
              <ImageUpload
                label="Logo *"
                testId="venue-logo"
                current={venue.logo_data_uri}
                onSelect={(f) => uploadImage("logo", f)}
              />
              <ImageUpload
                label="Cover photo"
                testId="venue-cover"
                current={venue.cover_data_uri}
                onSelect={(f) => uploadImage("cover", f)}
                wide
              />
            </div>
          </Card>

          {/* Basic info */}
          <VenueInfoCard venue={venue} onSave={patchVenue} saving={saving} />

          {/* Address */}
          <AddressCard venue={venue} onSave={patchVenue} saving={saving} />

          {/* Opening hours */}
          <OpeningHoursCard venue={venue} onSave={patchVenue} saving={saving} />
        </div>

        {/* Events */}
        <div className="mt-10">
          <EventsSection events={events} onAdd={addEvent} onDelete={deleteEvent} />
        </div>
      </div>
    </div>
  );
}

/* ------- shared cards ------- */

function Card({ title, testId, children }) {
  return (
    <div data-testid={testId} className="bg-[#0A0A0A] border border-white/10 p-6 md:p-8">
      <div className="text-xs uppercase tracking-[0.24em] text-[#D9A6F5] mb-5">{title}</div>
      {children}
    </div>
  );
}

function VenueInfoCard({ venue, onSave, saving }) {
  const [name, setName] = useState(venue.name);
  const [email, setEmail] = useState(venue.email || "");
  const [phone, setPhone] = useState(venue.phone || "");
  const [fab, setFab] = useState(venue.fabswingers_profile || "");
  const [swinghub, setSwinghub] = useState(venue.swinghub_profile || "");

  const dirty =
    name !== venue.name ||
    email !== (venue.email || "") ||
    phone !== (venue.phone || "") ||
    fab !== (venue.fabswingers_profile || "") ||
    swinghub !== (venue.swinghub_profile || "");

  const submit = (e) => {
    e.preventDefault();
    if (!dirty || !name.trim()) return;
    onSave({
      name: name.trim(),
      email: email.trim() || null,
      phone: phone.trim() || null,
      fabswingers_profile: fab.trim() || null,
      swinghub_profile: swinghub.trim() || null,
    });
  };

  return (
    <Card title="Contact & profiles" testId="venue-info-card">
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Venue name *">
          <Input data-testid="venue-info-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} required />
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Email">
            <Input data-testid="venue-info-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="info@venue.com" />
          </Field>
          <Field label="Contact number">
            <Input data-testid="venue-info-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} placeholder="07000 000 000" />
          </Field>
        </div>
        <Field label="FabSwingers profile URL">
          <Input data-testid="venue-info-fab" type="url" value={fab} onChange={(e) => setFab(e.target.value)} className={inputCls} placeholder="https://fabswingers.com/…" />
        </Field>
        <Field label="SwingHub profile URL">
          <Input data-testid="venue-info-swinghub" type="url" value={swinghub} onChange={(e) => setSwinghub(e.target.value)} className={inputCls} placeholder="https://swinghub.co.uk/…" />
        </Field>
        <button
          type="submit"
          disabled={saving || !dirty}
          data-testid="venue-info-save"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-40 px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : null}
          {dirty ? "Save changes" : "Saved"}
        </button>
      </form>
    </Card>
  );
}

function AddressCard({ venue, onSave, saving }) {
  const a = venue.address || {};
  const [line1, setLine1] = useState(a.line1 || "");
  const [line2, setLine2] = useState(a.line2 || "");
  const [city, setCity] = useState(a.city || "");
  const [postcode, setPostcode] = useState(a.postcode || "");
  const [country, setCountry] = useState(a.country || "United Kingdom");

  const dirty =
    line1 !== (a.line1 || "") ||
    line2 !== (a.line2 || "") ||
    city !== (a.city || "") ||
    postcode !== (a.postcode || "") ||
    country !== (a.country || "United Kingdom");

  const submit = (e) => {
    e.preventDefault();
    if (!dirty || !line1 || !city || !postcode) return;
    onSave({
      address: {
        line1: line1.trim(),
        line2: line2.trim() || null,
        city: city.trim(),
        postcode: postcode.trim(),
        country: country.trim() || "United Kingdom",
      },
    });
  };

  return (
    <Card title="Address" testId="venue-address-card">
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Line 1 *">
          <Input data-testid="venue-address-line1" value={line1} onChange={(e) => setLine1(e.target.value)} className={inputCls} required />
        </Field>
        <Field label="Line 2">
          <Input data-testid="venue-address-line2" value={line2} onChange={(e) => setLine2(e.target.value)} className={inputCls} />
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="City *">
            <Input data-testid="venue-address-city" value={city} onChange={(e) => setCity(e.target.value)} className={inputCls} required />
          </Field>
          <Field label="Postcode *">
            <Input data-testid="venue-address-postcode" value={postcode} onChange={(e) => setPostcode(e.target.value)} className={inputCls} required />
          </Field>
        </div>
        <Field label="Country">
          <Input data-testid="venue-address-country" value={country} onChange={(e) => setCountry(e.target.value)} className={inputCls} />
        </Field>
        <button
          type="submit"
          disabled={saving || !dirty}
          data-testid="venue-address-save"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-40 px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : null}
          {dirty ? "Save address" : "Saved"}
        </button>
      </form>
    </Card>
  );
}

function OpeningHoursCard({ venue, onSave, saving }) {
  const seed = () => {
    const existing = venue.opening_hours || [];
    return DAYS.map((_, i) => {
      const found = existing.find((h) => h.day === i);
      return found
        ? { day: i, open: found.open || "", close: found.close || "", closed: !!found.closed }
        : { day: i, open: "", close: "", closed: true };
    });
  };
  const [hours, setHours] = useState(seed);

  const dirty = useMemo(() => JSON.stringify(hours) !== JSON.stringify(seed()), [hours, venue]);

  const setDay = (i, patch) =>
    setHours((h) => h.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  const submit = (e) => {
    e.preventDefault();
    if (!dirty) return;
    const payload = hours.map((h) => ({
      day: h.day,
      open: h.closed ? null : h.open || null,
      close: h.closed ? null : h.close || null,
      closed: !!h.closed,
    }));
    onSave({ opening_hours: payload });
  };

  return (
    <Card title="Opening times" testId="venue-hours-card">
      <form onSubmit={submit} className="space-y-3" noValidate>
        {hours.map((row, i) => (
          <div key={i} className="grid grid-cols-[60px_1fr_1fr_auto] gap-3 items-center">
            <div className="text-sm font-semibold uppercase tracking-[0.18em] text-[#A3A3A3]">{DAYS[i]}</div>
            <Input
              data-testid={`venue-hours-${i}-open`}
              type="time"
              value={row.open}
              disabled={row.closed}
              onChange={(e) => setDay(i, { open: e.target.value })}
              className={inputCls}
            />
            <Input
              data-testid={`venue-hours-${i}-close`}
              type="time"
              value={row.close}
              disabled={row.closed}
              onChange={(e) => setDay(i, { close: e.target.value })}
              className={inputCls}
            />
            <label className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#A3A3A3] cursor-pointer">
              <input
                type="checkbox"
                data-testid={`venue-hours-${i}-closed`}
                checked={row.closed}
                onChange={(e) => setDay(i, { closed: e.target.checked })}
                className="accent-[#D9A6F5]"
              />
              Closed
            </label>
          </div>
        ))}
        <button
          type="submit"
          disabled={saving || !dirty}
          data-testid="venue-hours-save"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-40 px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors mt-2"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : null}
          {dirty ? "Save opening times" : "Saved"}
        </button>
      </form>
    </Card>
  );
}

function ImageUpload({ label, testId, current, onSelect, wide = false }) {
  const inputRef = useRef(null);
  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-[0.22em] text-[#A3A3A3] font-medium">{label}</div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        data-testid={`${testId}-upload-btn`}
        className={`w-full ${wide ? "aspect-[16/9]" : "aspect-square"} border border-dashed border-white/15 hover:border-[#D9A6F5]/60 bg-black flex items-center justify-center overflow-hidden transition-colors group cursor-pointer`}
      >
        {current ? (
          <img
            src={current}
            alt={label}
            data-testid={`${testId}-current`}
            className="max-h-full max-w-full object-contain"
          />
        ) : (
          <div className="flex flex-col items-center gap-2 text-[#A3A3A3] group-hover:text-[#D9A6F5]">
            <Upload size={20} />
            <div className="text-xs uppercase tracking-[0.22em]">Click to upload</div>
          </div>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        data-testid={`${testId}-file-input`}
        onChange={(e) => onSelect(e.target.files?.[0] || null)}
        className="hidden"
      />
    </div>
  );
}

function EventsSection({ events, onAdd, onDelete }) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("22:00");
  const [openTo, setOpenTo] = useState("");
  const [adding, setAdding] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title || !date || !time || adding) return;
    setAdding(true);
    try {
      const iso = new Date(`${date}T${time}:00`).toISOString();
      await onAdd({ title: title.trim(), starts_at: iso, open_to: openTo.trim() || null });
      setTitle("");
      setDate("");
      setTime("22:00");
      setOpenTo("");
    } catch {
      /* toast handled in caller */
    } finally {
      setAdding(false);
    }
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-GB", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="bg-[#0A0A0A] border border-white/10 p-6 md:p-8">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#D9A6F5] mb-6">
        <Calendar size={14} /> Events ({events.length})
      </div>

      <form
        onSubmit={submit}
        data-testid="venue-event-form"
        className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_2fr_auto] gap-3 mb-8"
        noValidate
      >
        <Input
          data-testid="venue-event-title"
          placeholder="Event title *"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className={inputCls}
          required
        />
        <Input
          data-testid="venue-event-date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className={inputCls}
          required
        />
        <Input
          data-testid="venue-event-time"
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          className={inputCls}
          required
        />
        <Input
          data-testid="venue-event-open-to"
          placeholder="Open to (e.g. Members only)"
          value={openTo}
          onChange={(e) => setOpenTo(e.target.value)}
          className={inputCls}
        />
        <button
          type="submit"
          data-testid="venue-event-submit"
          disabled={adding || !title || !date || !time}
          className="inline-flex items-center justify-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-40 px-5 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
        >
          {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add
        </button>
      </form>

      {events.length === 0 ? (
        <div className="text-sm text-[#A3A3A3]/60 uppercase tracking-[0.2em] text-center py-8">
          No events yet
        </div>
      ) : (
        <div className="space-y-2">
          {events.map((ev) => (
            <div
              key={ev.id}
              data-testid={`venue-event-row-${ev.id}`}
              className="group flex items-center justify-between gap-4 bg-black border border-white/10 p-4 hover:border-[#D9A6F5]/40 transition-colors"
            >
              <div className="min-w-0 flex-1">
                <div className="text-base font-semibold text-[#F5F5F0] truncate">{ev.title}</div>
                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#A3A3A3]">
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar size={11} className="text-[#D9A6F5]" /> {fmt(ev.starts_at)}
                  </span>
                  {ev.open_to && (
                    <span className="inline-flex items-center gap-1.5">
                      <Users size={11} className="text-[#D9A6F5]" /> {ev.open_to}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onDelete(ev.id)}
                data-testid={`venue-event-delete-${ev.id}`}
                aria-label="Delete event"
                className="opacity-0 group-hover:opacity-100 text-[#A3A3A3] hover:text-red-400 p-1 transition-opacity"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-[0.22em] text-[#A3A3A3] font-medium">{label}</div>
      {children}
    </div>
  );
}

function InviteUserButton({ venueId }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || sending) return;
    setSending(true);
    try {
      await adminApi.post(`/admin/venues/${venueId}/users`, { email: email.trim() });
      toast.success(`Invite sent to ${email.trim()}.`);
      setEmail("");
      setOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to send invite.";
      toast.error(typeof detail === "string" ? detail : "Invite failed.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="venue-invite-user-btn"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 border border-[#D9A6F5]/40 text-[#D9A6F5] hover:bg-[#D9A6F5] hover:text-black px-4 py-2 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
      >
        <Users size={12} /> Invite user
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="venue-invite-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setOpen(false)} aria-hidden />
          <div className="relative bg-[#0A0A0A] border border-white/10 w-full max-w-md p-8">
            <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-4">Invite venue user</div>
            <p className="text-sm text-[#A3A3A3] mb-5 leading-relaxed">
              They'll receive an email with a link to set their password and access the portal.
            </p>
            <form onSubmit={submit} className="space-y-4" data-testid="venue-invite-form" noValidate>
              <Input
                type="email"
                placeholder="owner@venue.com"
                data-testid="venue-invite-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                className={inputCls}
              />
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="flex-1 border border-white/20 text-[#F5F5F0] hover:border-[#D9A6F5] px-4 py-3 text-xs uppercase tracking-[0.22em] font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sending || !email}
                  data-testid="venue-invite-submit"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-40 px-4 py-3 text-xs uppercase tracking-[0.22em] font-bold"
                >
                  {sending ? <Loader2 size={14} className="animate-spin" /> : null}
                  Send Invite
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

const inputCls =
  "bg-black border-white/15 rounded-none h-11 text-[#F5F5F0] placeholder:text-[#A3A3A3]/60 focus-visible:ring-[#D9A6F5] focus-visible:ring-offset-0 w-full px-3";
